import { getStreams, getCatalog, getMeta } from "@/lib/stremio";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Play,
  ExternalLink,
  Info,
  Download,
  Loader2,
} from "lucide-react";
import { Link } from "react-router";
import { proxyUrl } from "@/lib/proxyUrl";
import { StreamsList } from "@/components/StreamsList";

export const Movie = () => {
  const { id } = useParams();
  if (!id) throw new Error("No id");

  const {
    data: movieData,
    isLoading: streamsLoading,
    error: streamsError,
  } = useQuery({
    queryKey: ["streams", id],
    queryFn: async () => {
      return await getStreams(`movie/${id}`);
    },
  });

  const {
    data: metaData,
    isLoading: metaLoading,
    error: metaError,
  } = useQuery({
    queryKey: ["meta", "movie", id],
    queryFn: async () => {
      return await getMeta("movie", id);
    },
  });

  const movieMeta = metaData;
  const isLoading = streamsLoading || metaLoading;
  const error = streamsError || metaError;

  return (
    <div className="min-h-screen bg-background">
      {movieMeta?.background && (
        <div className="fixed inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-background/30 to-background" />
          <img
            src={proxyUrl(movieMeta.background)}
            alt={movieMeta.name}
            className="w-full h-full object-cover opacity-30 fixed inset-0"
            style={{ objectPosition: "center top" }}
          />
        </div>
      )}

      <div className="container mx-auto p-8 relative z-10">
        <Button
          variant="ghost"
          className="mb-8"
          onClick={() => window.history.back()}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Go Back
        </Button>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : error ? (
          <Card className="bg-destructive/10 border-destructive">
            <CardContent className="p-6">
              <p className="text-destructive">Error: {error.message}</p>
            </CardContent>
          </Card>
        ) : (
          <div>
            <div className="flex gap-8 mb-8">
              {movieMeta && (
                <Card
                  className="overflow-hidden shrink-0"
                  style={{ width: "200px" }}
                >
                  <img
                    src={proxyUrl(movieMeta.poster)}
                    alt={movieMeta.name}
                    className="w-full aspect-[2/3] object-cover"
                  />
                </Card>
              )}

              <div>
                <h1 className="text-4xl font-bold mb-2">{movieMeta?.name}</h1>
                <div className="flex items-center gap-4 text-muted-foreground mb-4">
                  {movieMeta?.year && <span>{movieMeta.year}</span>}
                  {movieMeta?.runtime && <span>{movieMeta.runtime}</span>}
                </div>
              </div>
            </div>

            <StreamsList
              streams={movieData?.streams}
              isLoading={streamsLoading}
              error={streamsError}
              title="Available Streams"
            />
          </div>
        )}
      </div>
    </div>
  );
};
